import React from 'react'
import {
  XMarkIcon,
  StarIcon,
  UsersIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

interface PlanUpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  onUpgrade: () => void
  requiredPlan: string
  currentPlan: string
  feature: string
}

const PlanUpgradeModal: React.FC<PlanUpgradeModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  requiredPlan,
  currentPlan,
  feature,
}) => {
  if (!isOpen) {
    return null
  }

  const getPlanDisplayName = (plan: string) => {
    const planNames = {
      starter: 'Starter',
      growth: 'Growth',
      scale: 'Scale',
      enterprise: 'Enterprise',
    }
    return planNames[plan as keyof typeof planNames] || plan
  }

  const getPlanFeatures = (plan: string) => {
    switch (plan) {
      case 'growth':
        return [
          '팀 협업 기능 (3명)',
          '고급 통계 분석',
          '커스텀 도메인 (1개)',
          '월 25,000 클릭',
          '우선 지원',
        ]
      case 'scale':
        return [
          '팀 협업 기능 (10명)',
          '무제한 통계 분석',
          '커스텀 도메인 (5개)',
          '월 100,000 클릭',
          '전화 지원',
          'API 접근',
        ]
      case 'enterprise':
        return [
          '무제한 팀 협업',
          '모든 고급 기능',
          '무제한 커스텀 도메인',
          '무제한 클릭',
          '전담 지원',
          'SSO 통합',
          '화이트 라벨링',
        ]
      default:
        return ['모든 기본 기능']
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-purple-600">
                <StarIcon className="h-5 w-5 text-white" />
              </div>
            </div>
            <div className="ml-3">
              <h2 className="text-lg font-semibold text-gray-900">
                플랜 업그레이드 필요
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 transition-colors hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-100 to-purple-100">
              <UsersIcon className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="mb-2 text-xl font-semibold text-gray-900">
              {feature} 기능은 {getPlanDisplayName(requiredPlan)} 플랜부터 이용
              가능합니다
            </h3>
            <p className="text-gray-600">
              현재 플랜:{' '}
              <span className="font-medium text-gray-900">
                {getPlanDisplayName(currentPlan)}
              </span>
            </p>
          </div>

          {/* 플랜 혜택 */}
          <div className="mb-6 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50 p-4">
            <h4 className="mb-3 font-semibold text-gray-900">
              {getPlanDisplayName(requiredPlan)} 플랜 혜택
            </h4>
            <ul className="space-y-2">
              {getPlanFeatures(requiredPlan).map((feature, index) => (
                <li
                  key={index}
                  className="flex items-center text-sm text-gray-700"
                >
                  <CheckIcon className="mr-2 h-4 w-4 flex-shrink-0 text-green-500" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA 버튼들 */}
          <div className="flex flex-col space-y-3">
            <button
              onClick={onUpgrade}
              className="w-full transform rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 font-medium text-white transition-all duration-200 hover:scale-105 hover:from-blue-700 hover:to-purple-700"
            >
              지금 업그레이드하기
            </button>

            <button
              onClick={onClose}
              className="w-full rounded-lg bg-gray-100 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-200"
            >
              나중에 하기
            </button>
          </div>

          {/* 추가 정보 */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              언제든 플랜을 변경하거나 취소할 수 있습니다
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlanUpgradeModal
